import React, { useRef, useState } from 'react';

import { useQueryGetRelationActivity } from 'api/queries/relationActivity.query';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import Icon from 'components/common/Icons/base/Icon/Icon';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/relationships/relationships.module.scss';
import {
    TABS_OPTIONS,
    destructureData,
    generateTabs,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/relationships/relationships.utils';
import { AncestorsTableWrapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/relationships/tables/ancestorsTableWrapper';
import { DecedentsTableWrapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/relationships/tables/decedentsTableWrapper';
import { PredeccessorsTableWrapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/relationships/tables/predeccessorsTableWrapper';
import { SuccessorsTableWrapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/relationships/tables/successorsTableWrapper';
import { Tabs } from 'components/DesignSystem/Tabs';
import { CustomizedRoundIcon, CustomizedTooltip } from 'components/common';

export const Relationships = ({ id, activityType }: { id?: number | string; activityType?: string }) => {
    const [activeTab, setActiveTab] = useState<TABS_OPTIONS>(activityType === 'WBS' ? 'ANCESTORS' : 'PREDECESSORS');

    const { data, isLoading } = useQueryGetRelationActivity({
        taskId: id,
    });

    const { ancestors, decedents, predecessors, successors } = destructureData(data);
    const tabsArrData = generateTabs({ data, activityType });

    const predeccessorsTableRef = useRef(null);
    const successorsTableRef = useRef(null);
    const ancestorsTableRef = useRef(null);
    const decedentsTableRef = useRef(null);
    const [modalColumnsOpen, setModalColumnsOpen] = useState<boolean>(false);

    return (
        <div className={classes.relationshipsContainer}>
            {isLoading && <OverlayWithSpinner />}

            <WidgetWithTitle
                title={'Activity Relationships'}
                titleComponents={[
                    <CustomizedTooltip
                        key={'tableColumns'}
                        tooltipPosition={TooltipPosition.Top}
                        tooltipContent={'Customize table columns'}
                        triggerElement={
                            <CustomizedRoundIcon enableHover={true} onClick={() => setModalColumnsOpen(true)}>
                                <Icon name={'tune-filter'} />
                            </CustomizedRoundIcon>
                        }
                    />,
                ]}
            >
                <div data-testid={'table-container'} className={classes.tableContainer}>
                    <Tabs
                        tabs={tabsArrData}
                        value={activeTab}
                        onChange={(value) => setActiveTab(value as TABS_OPTIONS)}
                    />

                    {predecessors && activeTab === 'PREDECESSORS' && (
                        <PredeccessorsTableWrapper
                            data={predecessors}
                            modalColumnsOpen={modalColumnsOpen}
                            setModalColumnsOpen={setModalColumnsOpen}
                            ref={predeccessorsTableRef}
                        />
                    )}

                    {successors && activeTab === 'SUCCESSORS' && (
                        <SuccessorsTableWrapper
                            data={successors}
                            modalColumnsOpen={modalColumnsOpen}
                            setModalColumnsOpen={setModalColumnsOpen}
                            ref={successorsTableRef}
                        />
                    )}

                    {ancestors && activeTab === 'ANCESTORS' && (
                        <AncestorsTableWrapper
                            data={ancestors}
                            modalColumnsOpen={modalColumnsOpen}
                            setModalColumnsOpen={setModalColumnsOpen}
                            ref={ancestorsTableRef}
                        />
                    )}

                    {decedents && activeTab === 'DESCENDANTS' && (
                        <DecedentsTableWrapper
                            data={decedents}
                            modalColumnsOpen={modalColumnsOpen}
                            setModalColumnsOpen={setModalColumnsOpen}
                            ref={decedentsTableRef}
                        />
                    )}
                </div>
            </WidgetWithTitle>
        </div>
    );
};
